import { useState, useEffect, useCallback } from 'react';
import { Users, Zap, Loader2 } from 'lucide-react';
import { useLocation } from '@context/LocationContext';
import { useAuth } from '@context/AuthContext';
import { calculateDistance, formatDistance, getCategoryInfo } from '@utils/helpers';
import Avatar from '@components/ui/Avatar';
import api from '@utils/api';
import toast from 'react-hot-toast';

const PeopleLooking = () => {
  const { location, locationName, locationLoading } = useLocation();
  const { user } = useAuth();
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingWith, setStartingWith] = useState(null);

  const fetchIntents = useCallback(async () => {
    if (locationLoading) return;

    try {
      const { data } = await api.get('/intents/nearby', {
        params: { lng: location.lng, lat: location.lat, radius: 10 },
      });
      setIntents(data.intents || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [location.lat, location.lng, locationLoading]);

  useEffect(() => {
    if (locationLoading) {
      setLoading(true);
      return;
    }

    fetchIntents();
    const iv = setInterval(fetchIntents, 20000);
    return () => clearInterval(iv);
  }, [fetchIntents, locationLoading]);

  const handleStartTogether = async (intentId) => {
    if (!user) {
      toast.error('Please log in first');
      return;
    }
    try {
      setStartingWith(intentId);
      await api.post('/intents/start-together', {
        intentId,
        locationAddress: locationName || undefined,
      });
      toast.success('Vibe started!');
      fetchIntents();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start');
    } finally {
      setStartingWith(null);
    }
  };

  const visibleIntents = intents.filter((i) => i.userId?._id !== user?._id);

  if (loading || visibleIntents.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2.5">
        <Users size={14} className="text-violet-500" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400">
          People Looking for Vibes
        </h3>
      </div>

      <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-1 -mx-3 px-3 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {visibleIntents.map((intent) => {
          const dist = intent.location?.coordinates
            ? calculateDistance(
                location.lat, location.lng,
                intent.location.coordinates[1], intent.location.coordinates[0]
              )
            : null;
          const cat = getCategoryInfo(intent.activityType);

          return (
            <div
              key={intent._id}
              className="shrink-0 w-56 rounded-2xl border border-surface-200/60 bg-white p-3.5 dark:border-surface-800 dark:bg-surface-900/80"
            >
              <div className="flex items-center gap-2.5 mb-2.5">
                <Avatar name={intent.userId?.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-surface-900 dark:text-white truncate">{intent.userId?.name}</p>
                  <p className="text-[11px] text-surface-400">
                    {dist !== null && formatDistance(dist) + ' away'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mb-3">
                <span className="text-base">{cat.emoji}</span>
                <span className="text-xs font-semibold text-surface-600 dark:text-surface-300">wants {cat.label}</span>
              </div>
              <button
                onClick={() => handleStartTogether(intent._id)}
                disabled={startingWith === intent._id}
                className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-brand-500 to-violet-500 py-2 text-xs font-bold text-white transition-all hover:shadow-md active:scale-95 disabled:opacity-50"
              >
                {startingWith === intent._id ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <Zap size={12} className="fill-white" />
                    Start Together
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PeopleLooking;
